import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatExpansionPanelActionRow } from '@angular/material/expansion';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatOption } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { map, Observable, startWith } from 'rxjs';
import { Logger } from '../../../../../../../infrastructure/logging/logger';
import { Currency } from '../../../../../../currencies/models/Currency';
import { CurrenciesService } from '../../../../../../currencies/services/currencies.service';
import { PriceRatesService } from '../../../../../../price-rates/services/price-rates.service';
import { Underwriter } from '../../../../../../underwriters/models/Underwriter';
import { PolicyPrice } from '../../../../../models/Policy';

@Component({
  selector: 'app-select-policy-underwriter-dialog',
  templateUrl: './select-policy-underwriter-dialog.component.html',
  styleUrls: ['./select-policy-underwriter-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatOption,
    ReactiveFormsModule,
    MatIcon,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatOptionModule,
    MatExpansionPanelActionRow
  ]
})
export class SelectPolicyUnderwriterDialogComponent implements OnInit, AfterViewInit {

  priceTypesArray: any[] = [
    { id: 1, display_text: 'Underwriter' },
    { id: 2, display_text: 'Property Manager' }
  ]

  pricingModels: any[] = [
    { id: 1, display_text: 'Tiered Nights' },
    { id: 2, display_text: 'Per Night' }
  ]

  priceRateForm!: FormGroup;
  tieredNightForm!: FormGroup;
  perNightForm!: FormGroup;
  selectedUnderwriter!: Underwriter;
  availableUnderwriters: Underwriter[] = [];
  priceRates: PolicyPrice[] = [];
  currencies!: Currency[];

  enterNewPriceRateForm: boolean = false;
  numberOfNightsToInput: number = 0;
  underwriterControl = new FormControl(undefined, [Validators.required, Validators.pattern(/[\S]/), this.requireUnderwriterMatch.bind(this)]);
  filteredUnderwriters!: Observable<any[]>;
  priceRateControl = new FormControl(undefined, [Validators.required, Validators.pattern(/[\S]/), this.requirePriceRateMatch.bind(this)]);
  filteredPriceRates!: Observable<any[]>;
  underwriterSelect!: Underwriter | undefined;
  priceRateSelect!: PolicyPrice | undefined;

  invalidForm: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<SelectPolicyUnderwriterDialogComponent>,
    private formBuilder: FormBuilder,
    private currenciesService: CurrenciesService,
    private priceRatesService: PriceRatesService,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.initForms();
    this.loadData();
    this.availableUnderwriters = this.data.underwriters;
    this.priceRates = this.data.priceRates;
    this.selectedUnderwriter = this.data.selectedUnderwriter;

    if (this.availableUnderwriters.length === 0) {
      this.underwriterControl.disable();
    }

    if (this.selectedUnderwriter.id) {
      this.patchFormValues(this.selectedUnderwriter);
    }
  }

  ngAfterViewInit() {
    this.filteredUnderwriters = this.underwriterControl.valueChanges.pipe(
      startWith(''),
      map(value => this._underwriterFilter(value || '')),
    );

    this.filteredPriceRates = this.priceRateControl.valueChanges.pipe(
      startWith(''),
      map(value => this._priceRateFilter(value || '')),
    );

    this.invalidForm = false;
  }

  private requireUnderwriterMatch(control: FormControl): ValidationErrors | null {
    const selection = control.value;
    if (this.availableUnderwriters && this.availableUnderwriters.indexOf(selection) < 0) {
      this.invalidForm = true;
      return { requireMatch: true };
    } else {
      this.invalidForm = false;
    }
    return null;
  }

  private requirePriceRateMatch(control: FormControl): ValidationErrors | null {
    const selection: any = control.value;
    if (this.priceRates && this.priceRates.indexOf(selection) < 0) {
      this.invalidForm = true;
      return { requireMatch: true };
    } else {
      this.invalidForm = false;
    }
    return null;
  }

  private _underwriterFilter(value: string): Underwriter[] {
    return this.availableUnderwriters.filter(underwriter => underwriter.name.toLowerCase().includes(value.toString().toLowerCase()));
  }

  private _priceRateFilter(value: any): PolicyPrice[] {
    return this.priceRates.filter(rate => rate.name.toLowerCase().includes(value.toString().toLowerCase()));
  }

  async patchFormValues(selectedUnderwriter: any) {
    let selectedPriceRate: any = this.priceRates.find(rate => rate.id === selectedUnderwriter.price_rate_id);
    this.underwriterControl.setValue(selectedUnderwriter);
    this.priceRateControl.setValue(selectedPriceRate);
  }

  async loadData() {
    const currenciesResponse = await this.currenciesService.getCurrencies();
    this.currencies = currenciesResponse;
  }

  initForms() {
    this.initPriceRateForm(); 
  }

  initPriceRateForm() {
    this.initSubPriceRateForms();
    this.priceRateForm = this.formBuilder.group({
      name: ['', Validators.required],
      type_id: ['', Validators.required],
      pricing_model_id: ['', Validators.required],
      currency_id: ['', Validators.required],
      deductible: ['', Validators.required],
      description: ['']
    });
  }

  initSubPriceRateForms() {
    this.tieredNightForm = this.formBuilder.group({
      night_ranges: this.formBuilder.array([
        this.formBuilder.group({
          name: ['', Validators.required],
          number_of_nights_from: [{ value: 1, disabled: true }, [Validators.required]],
          number_of_nights_to: ['', Validators.required],
          price: ['', Validators.required]
        })
      ]),
      extra_night_price: ['', Validators.required]
    });

    this.perNightForm = this.formBuilder.group({
      nightly_price: ['', Validators.required]
    });
  }

  onEnterNewPriceRateClick() {
    this.enterNewPriceRateForm = true;
  }

  submitUnderwriter() {
    if (this.enterNewPriceRateForm) {
      this.buildPriceRatePayload();
    } else {
      let payload;
      if (this.selectedUnderwriter.id) {
        payload = this.buildUpdateUnderwriterPayload();
        this.resetSelects();
        this.dialogRef.close(payload);
      } else {
        payload = this.buildCreateUnderwriterPayload();
        this.resetSelects();
        this.dialogRef.close(payload);
      }
    }
  }

  async buildPriceRatePayload() {
    try {
      let priceRate: any = {
        ...this.priceRateForm.value
      }

      if (this.priceRateForm.get('pricing_model_id')?.value === '1') {
        priceRate = { ...priceRate, ...this.tieredNightForm.getRawValue() }
        delete priceRate.perNightForm;
      }
      if (this.priceRateForm.get('pricing_model_id')?.value === '2') {
        priceRate.price = this.perNightForm.get('nightly_price')?.value;
        delete priceRate.tieredNightForm;
      }

      const priceRateId = (await this.priceRatesService.createPriceRate(priceRate)).id;

      let payload;
      if (this.selectedUnderwriter.id) {
        payload = this.buildUpdateUnderwriterPayload(priceRateId);
      } else {
        payload = this.buildCreateUnderwriterPayload(priceRateId);
      }
      
      this.dialogRef.close(payload);
      this.resetSelects();
      this.priceRateForm.reset();
      this.tieredNightForm.reset();
      this.perNightForm.reset();
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while submitting Price Rate', 'Error while submitting Price Rate');
    }
  }

  buildCreateUnderwriterPayload(priceRateId?: number) {
    let createUnderwriterPayload = {
      policy_id: Number(this.data.policyId),
      policy_coverage_id: Number(this.data.policyId),
      status_id: 1,
      underwriter_id: this.underwriterSelect?.id.toString(),
      policy_price_id: priceRateId ? priceRateId : this.priceRateSelect?.id.toString(),
      original_id: this.selectedUnderwriter.id
    }
    return createUnderwriterPayload;
  }

  buildUpdateUnderwriterPayload(priceRateId?: number) {
    let updateUnderwriterPayload = {
      policy_id: Number(this.data.policyId),
      policy_coverage_id: Number(this.data.policyId),
      status_id: 1,
      underwriter_id: this.underwriterSelect?.id.toString(),
      policy_price_id: priceRateId ? priceRateId : this.priceRateSelect?.id.toString(),
      account_id: this.selectedUnderwriter.account_id
    }
    return updateUnderwriterPayload;
  }

  nightRanges(): FormArray {
    return this.tieredNightForm.get('night_ranges') as FormArray;
  }

  newNightRange(): FormGroup {
    return this.formBuilder.group({
      name: ['', Validators.required],
      number_of_nights_from: [{ value: Number(this.numberOfNightsToInput) + 1, disabled: true }, Validators.required],
      number_of_nights_to: ['', Validators.required],
      price: ['', Validators.required]
    });
  }

  addNightRange() {
    this.nightRanges().push(this.newNightRange());
  }

  removeNightRange(i: number) {
    this.nightRanges().removeAt(i);
  }

  setTieredNightName(i: number, nightsTo: any) {
    this.numberOfNightsToInput = nightsTo;

    let nightRangeArray = this.tieredNightForm.get("night_ranges") as FormArray;
    nightRangeArray.controls[i].patchValue({
      name: nightsTo + '-nights'
    });

    nightRangeArray.controls[i + 1].patchValue({
      number_of_nights_from: Number(nightsTo) + 1
    });
  }

  onUnderwriterSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.underwriterSelect = event.option.value;
  }

  onPriceRateSelectionChanged(event: MatAutocompleteSelectedEvent) {
    this.priceRateSelect = event.option.value;
  }

  canSubmitUnderwriter(): boolean {
    if (!this.invalidForm) {
      if (!this.enterNewPriceRateForm) {
        if (this.underwriterSelect && this.priceRateSelect || this.selectedUnderwriter.id) {
          return true;
        }
      } else {
        return (
          this.underwriterSelect &&
          this.priceRateForm.valid && 
          this.tieredNightForm.valid || 
          this.perNightForm.valid
        )
      }
    }
    return false;
  }

  displayUnderwriterFn(underwriter: Underwriter): string {
    return underwriter && underwriter.name ? underwriter.name : '';
  }

  displayPriceRateFn(priceRate: PolicyPrice): string {
    return priceRate && priceRate.name ? priceRate.name : '';
  }

  cancel() {
    this.resetSelects();
    this.dialogRef.close(false);
  }

  resetSelects() {
    this.underwriterSelect = undefined;
    this.priceRateSelect = undefined;
  }
}